// ─── Shared constants and pure helpers for the Accounts view ─────────────────

export { LICENSE_TIERS, PAGE_SIZES } from './shared.constants.js';

// ── Filter options ────────────────────────────────────────────────────────────

export const PROMPTS_PRESETS = [
  { label: 'Any',      value: ''       },
  { label: '0 – 10',   value: '0-10'  },
  { label: '10 – 50',  value: '10-50' },
  { label: '50 – 100', value: '50-100'},
  { label: '100+',     value: '100+'  },
];

export const FILTER_DEFAULTS = {
  licenseTiers:        [],
  practiceAreasFilter: [],
  avgPromptsPreset:    '',
  dateFrom:            '',
  dateTo:              '',
};

export function hasActiveFilter(f) {
  return (
    f.licenseTiers.length > 0        ||
    f.practiceAreasFilter.length > 0 ||
    f.avgPromptsPreset !== ''         ||
    f.dateFrom !== ''                 ||
    f.dateTo   !== ''
  );
}

// ── Column definitions ────────────────────────────────────────────────────────

export const COLS = [
  { key: 'name',                      label: 'NAME',             sortable: true  },
  { key: 'email',                     label: 'EMAIL',            sortable: true  },
  { key: '_date_created_str',         label: 'DATE CREATED',     sortable: true  },
  { key: 'active',                    label: 'STATUS',           sortable: true  },
  { key: 'license',                   label: 'LICENSE',          sortable: false },
  { key: 'practice_areas',            label: 'PRACTICE AREAS',   sortable: false },
  { key: 'latest_prompts_per_month',  label: 'LATEST PROMPT/MO', sortable: true  },
  { key: 'average_prompts_per_month', label: 'AVG PROMPT/MO',    sortable: true  },
  { key: '_actions',                  label: 'ACTIONS',          sortable: false },
];

// ── UI strings ────────────────────────────────────────────────────────────────

export const STRINGS = {
  // Page
  pageTitle:    'Customer Accounts',
  pageSubtitle: 'Manage and monitor all customer accounts',

  // Search / table
  searchPlaceholder: 'Search by name or email...',
  emptyMessage:      'No accounts found.',

  // Filter chips (base labels)
  licenseChip:     'License',
  practiceChip:    'Practice Areas',
  avgPromptsChip:  'Avg Prompts/mo',
  dateCreatedChip: 'Date Created',
  clearAll:        'Clear All',

  // Dropdown labels & content
  licenseDropdownLabel:      'License tier',
  practiceDropdownLabel:     'Practice areas',
  practiceSearchPlaceholder: 'Search areas...',
  practiceEmpty:             'No areas found',
  avgPromptsDropdownLabel:   'Average prompts / mo',
  dateDropdownLabel:         'Date created',
  dateFrom:                  'From',
  dateTo:                    'To',

  // Kebab actions
  kebabView:   'View',
  kebabEdit:   'Edit',
  kebabDelete: 'Delete',

  // Edit modal
  editModalTitle:      'Edit Account',
  editPlanLabel:       'Plan',
  editStatusLabel:     'Status',
  editStatusActive:    'Active',
  editStatusCancelled: 'Cancelled',
  editModalSave:       'Save',

  // Delete modal
  deleteModalTitle: 'Delete Account',
  deleteModalBtn:   'Delete Account',
};

// ── Chip label helpers (pure fns of filter state) ─────────────────────────────

export function licenseChipLabel(filters) {
  if (filters.licenseTiers.length === 0) return STRINGS.licenseChip;
  if (filters.licenseTiers.length === 1) return `${STRINGS.licenseChip} · ${filters.licenseTiers[0]}`;
  return `${STRINGS.licenseChip} · ${filters.licenseTiers.length}`;
}

export function practiceAreasChipLabel(filters) {
  if (filters.practiceAreasFilter.length === 0) return STRINGS.practiceChip;
  if (filters.practiceAreasFilter.length === 1) return `Areas · ${filters.practiceAreasFilter[0]}`;
  return `Areas · ${filters.practiceAreasFilter.length}`;
}

export function avgPromptsChipLabel(filters) {
  if (!filters.avgPromptsPreset) return STRINGS.avgPromptsChip;
  const preset = PROMPTS_PRESETS.find(p => p.value === filters.avgPromptsPreset);
  return `${STRINGS.avgPromptsChip} · ${preset?.label || filters.avgPromptsPreset}`;
}

export function dateChipLabel(filters) {
  const { dateFrom, dateTo } = filters;
  if (!dateFrom && !dateTo) return STRINGS.dateCreatedChip;
  if (dateFrom && dateTo)   return `Date · ${dateFrom} – ${dateTo}`;
  if (dateFrom)             return `Date · from ${dateFrom}`;
  return `Date · to ${dateTo}`;
}
